<template>
  <Modal
    :show="show"
    :show-error="hasSendEmailError || hasSubmitFormError"
    width="380"
    @close="close"
    @close-error="hasSendEmailError = hasSubmitFormError = false"
  >
    <template #activator>
      <slot name="activator" />
    </template>

    <template #title>
      Share Your Document
    </template>

    <template #content>
      <v-form
        ref="form"
        v-model="valid"
      >
        <template v-if="modalContent === 'shareForm'">
          <p
            v-if="!(hasShareUsers && !isLinked)"
            class="mb-6"
          >
            Create a share link to allow someone else to edit your document
          </p>

          <div v-if="hasShareUsers && !isLinked">
            <p class="mb-6 share-job-modal-draft">
              Invite a team member to review and edit this job only. Select an existing user
              from the dropdown OR invite a new user using the form below.
            </p>
            <DpplTreeselect
              v-model="userSelectModel"
              :disabled="firstName.length > 0 || lastName.length > 0 || email.length > 0"
              class="pb-6"
              :max-height="200"
              :placeholder="placeHolderText"
              :options="validUsersConverted"
              label-key="name"
              id-key="email"
              @add-snackbar="addErrorSnackbar"
            >
              <template #option-label="{ option }">
                <div class="d-flex flex-column align-start justify-start">
                  <p class="treeselect-name">
                    {{ option.name }}
                  </p>
                  <p class="treeselect-email">
                    {{ option.email }}
                  </p>
                </div>
              </template>
            </DpplTreeselect>
            <v-divider class="mb-6 mt-1" />
          </div>
          <dppl-text-field
            v-model.trim="firstName"
            class="mb-1"
            label="First Name"
            :rules="emailExisting.length > 0 ? [] : firstNameRules"
            :disabled="emailExisting.length > 0"
            tile
          />
          <dppl-text-field
            v-model.trim="lastName"
            class="mb-1"
            label="Last Name"
            :rules="emailExisting.length > 0 ? [] : lastNameRules"
            :disabled="emailExisting.length > 0"
            tile
          />
          <dppl-text-field
            v-model.trim="email"
            class="mb-1"
            label="Email Address"
            :rules="emailExisting.length > 0 ? [] : emailRules"
            :disabled="emailExisting.length > 0"
            tile
          />
        </template>

        <template
          v-if="modalContent === 'hasShareLink'"
        >
          <p class="mb-4">
            Edit link successfully created. Select option below
            to notify {{ `${firstName} ${lastName}` }}.
          </p>
          <p class="text-break">
            {{ shareUrl }}
          </p>
        </template>
        <div
          v-if="modalContent === 'hasExistingUsersConfirmation'"
          class="mt-2 pb-0"
        >
          <div class="mb-4">
            Invite{{ processedUsers.length > 1 ? 's' : '' }}
            sent to {{ processedUsers.join(", ") }}
          </div>
        </div>
      </v-form>
    </template>

    <template #actions>
      <template
        v-if="modalContent === 'shareForm'"
      >
        <v-spacer />
        <dppl-auto-sized-button
          color="primary"
          type="submit"
          :disabled="submitting"
          @click="submitForm"
        >
          Create Link
        </dppl-auto-sized-button>
      </template>

      <template v-if="modalContent === 'hasShareLink'">
        <DpplAutoSizedButton
          :color="inviteLinkCopied ? 'success' : 'primary'"
          @click="copyShareLink"
        >
          {{ inviteLinkCopied ? 'Link Copied!' : 'Copy Edit Link' }}
        </DpplAutoSizedButton>
        <v-spacer />
        <DpplAutoSizedButton
          color="primary"
          :disabled="inviteEmailSent || submitting"
          @click="sendEmail"
        >
          {{ inviteEmailSent ? 'Email Sent' : 'Send Email' }}
        </DpplAutoSizedButton>
      </template>

      <template v-if="modalContent === 'hasExistingUsersConfirmation'">
        <v-spacer />
        <DpplAutoSizedButton
          color="primary"
          :disabled="inviteEmailSent"
          @click="close"
        >
          Close
        </DpplAutoSizedButton>
      </template>
    </template>

    <template
      v-if="showInvite"
      #footer
    >
      <hr class="mb-4">
      <div>
        <p class="pt-2 pb-6 px-6">
          <router-link
            class="link-text"
            :to="{ name: 'platform-permissions' }"
          >
            Invite someone
          </router-link> to join the {{ companyName }} account.
        </p>
      </div>
    </template>

    <template #error>
      <template v-if="hasSendEmailError">
        Send email error
      </template>
      <template v-if="hasSubmitFormError">
        Submit form error
      </template>
    </template>
  </Modal>
</template>

<script>
import { vueShareLink } from '@/api/vue';
import Modal from '@/components/shared/Modal.vue';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import validation from '@/helpers/validation';
import { convertValidUsers } from '@/helpers/formatters';
import { mapActions } from 'pinia';
import { useTracking } from '@/stores/tracking/tracking';
import { useEditor } from '@/stores/editor/editor';
import DpplTreeselect from '@/components/datapeople/DpplTreeselect.vue';
import { useAppSnackbar } from '@/stores/app/snackbar';

export default {
  name: 'ShareModal',

  components: {
    Modal,
    DpplAutoSizedButton,
    DpplTextField,
    DpplTreeselect,
  },
  props: {
    show: { type: Boolean, default: false },
    hasShareUsers: { type: Boolean, default: false },
    validUsers: { type: Array, default: () => [] },
    isLinked: { type: Number, default: 0 },
    jobId: { type: [String, Number], default: '' },
    showInvite: { type: Boolean, default: false },
    companyName: { type: String, default: '' },
  },
  emits: ['close'],

  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      userSelectValue: [],
      emailExisting: [],
      valid: false,
      modalContent: 'shareForm',
      firstNameRules: validation.firstNameRules(),
      lastNameRules: validation.lastNameRules(),
      emailRules: validation.emailRules(),
      shareUrl: '',
      processedUsers: [],
      inviteEmailSent: false,
      inviteLinkCopied: false,
      hasSubmitFormError: false,
      hasSendEmailError: false,
      submitting: false,
    };
  },
  computed: {
    userSelectModel: {
      get() {
        return this.userSelectValue;
      },
      set(value) {
        this.userSelectValue = value;
        this.emailExisting = value.map(({ email }) => email);
      },
    },
    validUsersConverted() {
      return convertValidUsers(this.validUsers);
    },
    placeHolderText() {
      return this.validUsersConverted.length > 100 ? 'Type to begin search' : 'Select users';
    },
  },
  methods: {
    ...mapActions(useEditor, ['copyToClipboard']),
    ...mapActions(useAppSnackbar, ['addErrorSnackbar']),
    async submitForm() {
      if (this.submitting) {
        return;
      }
      this.submitting = true;

      const {
        jobId, firstName, lastName, email, emailExisting,
      } = this;

      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          const responseJSON = await vueShareLink({
            jobId, firstName, lastName, email, emailExisting, sendEmail: false,
          });

          if (responseJSON.type === 'regular') {
            this.modalContent = 'hasShareLink';
            this.shareUrl = responseJSON.url;
          } else if (responseJSON.type === 'draft') {
            this.modalContent = 'hasExistingUsersConfirmation';
            this.processedUsers = responseJSON.users;
          }

          useTracking().shareJob({
            job: this, props: { firstName, lastName, email: email || emailExisting },
          });
        }
      } catch (e) {
        this.hasSubmitFormError = true;
      } finally {
        this.submitting = false;
      }
    },
    async sendEmail() {
      if (this.submitting) {
        return;
      }
      this.submitting = true;

      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          await vueShareLink({
            jobId: this.jobId,
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            emailExisting: this.emailExisting,
            sendEmail: true,
          });
          this.inviteEmailSent = true;
        }
      } catch (e) {
        this.hasSendEmailError = true;
      } finally {
        this.submitting = false;
      }
    },
    copyShareLink() {
      this.copyToClipboard({ content: this.shareUrl });
      this.inviteLinkCopied = true;
      setTimeout(() => {
        this.inviteLinkCopied = false;
      }, 3000);
    },
    close() {
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.emailExisting = [];
      this.inviteEmailSent = false;
      this.inviteLinkCopied = false;
      this.modalContent = 'shareForm';
      this.hasSendEmailError = false;
      this.hasSubmitFormError = false;
      this.$refs.form.resetValidation();
      this.$emit('close');
    },
  },
};
</script>

<style lang="less" scoped>
.treeselect-name {
  font-weight: 400;
}

.treeselect-email {
  font-weight: 300;
  padding-top: 2px;
}
</style>
